/**
 * Chat Input Component
 *
 * Input field for sending messages with send button and keyboard support.
 * Styles match original eaidev.css exactly.
 */

import { LitElement, html, css } from 'lit';

/**
 * ChatInput - Message input component
 * @element chat-input
 * @fires send-message - When a message is submitted
 */
export class ChatInput extends LitElement {
    static properties = {
        value: { type: String, state: true },
        placeholder: { type: String },
        disabled: { type: Boolean, reflect: true },
        sendDisabled: { type: Boolean, attribute: 'send-disabled' },
        maxLength: { type: Number, attribute: 'max-length' }
    };

    static styles = css`
        :host {
            display: block;
            font-family: 'Inter', sans-serif;
        }

        /* Chat Input Section */
        .chatbox__input {
            display: flex;
            align-items: center;
            background: #ffffff;
            border: 1px solid #d1d5db;
            border-radius: 10px;
            padding: 0 4px;
            min-height: 40px;
            box-sizing: border-box;
        }

        /* Textarea field — 1 line default, auto-resizes when text wraps */
        .chatbox__input textarea {
            flex: 1;
            padding: 8px 6px;
            border: none;
            border-radius: 0;
            background: transparent;
            outline: none;
            font-size: 14px;
            color: #111827;
            font-family: 'Inter', sans-serif;
            resize: none;
            overflow: hidden;
            height: 21px;
            max-height: 120px;
            line-height: 1.5;
        }

        .chatbox__input textarea::placeholder {
            color: #999;
        }

        /* Send Button - from eaidev.css lines 266-276 */
        .chatbox__input button {
            background: none;
            border: none;
            padding: 4px;
            cursor: pointer;
            display: flex;
            align-items: center;
            justify-content: center;
        }

        /* Send Button Icon - from eaidev.css lines 278-284 */
        .chatbox__input button svg {
            width: 28px;
            height: 28px;
            fill: #111111;
            transition: fill 0.3s ease;
        }

        /* Disabled state - from eaidev.css lines 286-290 */
        .chatbox__input.disabled {
            opacity: 0.5;
            pointer-events: none;
            background-color: #f3f4f6;
        }

        /* Send-disabled state - only disables the send button */
        .chatbox__input button:disabled {
            opacity: 0.5;
            pointer-events: none;
            cursor: not-allowed;
        }

        /* Character counter */
        .chatbox__counter {
            margin-top: 4px;
            text-align: right;
            font-size: 12px;
            color: #6b7280;
            font-family: 'Inter', sans-serif;
        }

        .chatbox__counter.at-limit {
            color: #dc2626;
        }
    `;

    constructor() {
        super();
        this.value = '';
        this.placeholder = 'Message...';
        this.disabled = false;
        this.sendDisabled = false;
        this.maxLength = 512;
    }

    get _isSendBlocked() {
        return this.disabled || this.sendDisabled;
    }

    render() {
        const length = this.value.length;
        const atLimit = length >= this.maxLength;
        return html`
            <div class="chatbox__input ${this.disabled ? 'disabled' : ''}">
                <textarea
                    id="chatInput"
                    .value=${this.value}
                    placeholder=${this.placeholder}
                    ?disabled=${this.disabled}
                    maxlength=${this.maxLength}
                    rows="1"
                    @input=${this._handleInput}
                    @keydown=${this._handleKeydown}
                    aria-label="Chat message input"
                ></textarea>
                <button
                    id="sendButton"
                    ?disabled=${this._isSendBlocked}
                    @click=${this._handleSend}
                    aria-label="Send message"
                >
                    ${this._renderSendIcon()}
                </button>
            </div>
            <div
                class="chatbox__counter ${atLimit ? 'at-limit' : ''}"
                aria-live="polite"
            >${length}/${this.maxLength}</div>
        `;
    }

    /**
     * Render send button SVG - exact copy from app.js getSendButtonSVG()
     */
    _renderSendIcon() {
        return html`
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <rect width="24" height="24" rx="12" fill="black"/>
                <path d="M8 12L6 6L18.6667 12M8 12L6 18L18.6667 12M8 12H18.6667" stroke="#FAFAFA" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
            </svg>
        `;
    }

    /**
     * Handle input changes
     */
    _handleInput(e) {
        this.value = e.target.value;
        // Reset to 1 line and re-measure
        e.target.style.overflow = 'hidden';
        e.target.style.height = '0';
        const scrollH = e.target.scrollHeight;
        // 37px = approximate single-line scrollHeight (21px content + padding)
        if (scrollH <= 37) {
            e.target.style.height = '21px';
        } else {
            e.target.style.height = `${Math.min(scrollH, 120)}px`;
        }
        if (scrollH > 120) {
            e.target.style.overflow = 'auto';
        }
    }

    /**
     * Handle keyboard events
     */
    _handleKeydown(e) {
        // Send on Enter, newline on Shift+Enter
        if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            this._handleSend();
        }
    }

    /**
     * Handle send button click
     */
    _handleSend() {
        const message = this.value.trim();

        if (!message || this._isSendBlocked) {
            return;
        }

        // Validate input
        if (!this._validateInput(message)) {
            return;
        }

        this.dispatchEvent(new CustomEvent('send-message', {
            detail: { message },
            bubbles: true,
            composed: true
        }));

        // Clear input and reset textarea height to single line
        this.value = '';
        const textarea = this.shadowRoot?.querySelector('textarea');
        if (textarea) {
            textarea.style.height = '21px';
            textarea.style.overflow = 'hidden';
        }
    }

    /**
     * Validate user input for basic sanity checks
     * Note: This is defense-in-depth only. The primary XSS protection
     * is handled by DOMPurify in message-bubble.js during rendering.
     * Server-side validation is also required for full security.
     */
    _validateInput(input) {
        // Check for obvious script injection attempts
        // This is not a complete security measure - DOMPurify handles actual sanitization
        const dangerousPatterns = [
            /<script[\s>]/i,           // Script tags with space or closing bracket
            /javascript\s*:/i,          // javascript: protocol with optional whitespace
            /on(?:error|load|click|mouse\w*|key\w*|focus|blur|change|submit)\s*=/i, // Event handlers
            /<iframe[\s>]/i,           // Iframe tags
            /<object[\s>]/i,           // Object tags
            /<embed[\s>]/i,            // Embed tags
            /<svg[\s>].*?on\w+\s*=/is, // SVG with event handlers
            /&#x?[0-9a-f]+;/i,         // HTML encoded characters (potential bypass)
            /data\s*:/i                 // data: protocol
        ];

        for (const pattern of dangerousPatterns) {
            if (pattern.test(input)) {
                // Log for monitoring but don't expose details to user
                if (typeof console !== 'undefined' && console.warn) {
                    console.warn('Input validation: potentially unsafe pattern detected');
                }
                return false;
            }
        }

        // Check input length
        if (input.length > this.maxLength) {
            return false;
        }

        return true;
    }

    /**
     * Focus the input field
     */
    focus() {
        const input = this.shadowRoot?.querySelector('textarea');
        input?.focus();
    }

    /**
     * Clear the input field
     */
    clear() {
        this.value = '';
        const textarea = this.shadowRoot?.querySelector('textarea');
        if (textarea) {
            textarea.style.height = '21px';
            textarea.style.overflow = 'hidden';
        }
    }
}

customElements.define('chat-input', ChatInput);
